import { useCallback } from "react";

import {
  UpdateProgramReviewGoalInput,
  UpdateProgramReviewTargetInput,
  useUpdateProgramReviewGoalMutation,
  useUpdateProgramReviewTargetMutation,
} from "@api/graphql/types-and-hooks";
import { useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface UseInlineGoalEditProps {
  goalId?: string;
  onSuccess?: () => void;
  targetId?: string;
}

export interface SaveFieldsResult {
  success: boolean;
  error?: string;
}

export const useInlineGoalEdit = ({ goalId = "", targetId = "", onSuccess }: UseInlineGoalEditProps) => {
  const updateProgramReviewGoalMutation = useUpdateProgramReviewGoalMutation({});
  const updateProgramReviewTargetMutation = useUpdateProgramReviewTargetMutation({});

  const { getProgramReviewGoalsQuery } = useProgrammingReviewData();

  const saveGoalFields = useCallback(
    async (fields: Partial<UpdateProgramReviewGoalInput>): Promise<SaveFieldsResult> => {
      // Validate goalName if it's being updated
      if ("title" in fields && (!fields.title || fields.title.trim() === "")) {
        return { success: false, error: "Goal name is required" };
      }

      try {
        const response = await updateProgramReviewGoalMutation.mutateAsync({
          input: {
            goalId,
            ...fields,
          },
        });

        if (response.updateProgramReviewGoal?.status) {
          // Await refetch to ensure the data is updated before continuing
          // The selectedProgramReviewGoal will automatically update since it's derived from the query data
          await getProgramReviewGoalsQuery.refetch();
          onSuccess?.();
          return { success: true };
        }

        return { success: false, error: response.updateProgramReviewGoal?.message || "Failed to save changes" };
      } catch (error) {
        console.error("Error when saving goal fields:", error);
        return { success: false, error: "Failed to save changes" };
      }
    },
    [updateProgramReviewGoalMutation, goalId, getProgramReviewGoalsQuery, onSuccess],
  );

  const saveTargetFields = useCallback(
    async (fields: Partial<UpdateProgramReviewTargetInput>): Promise<SaveFieldsResult> => {
      // Validate target name if it's being updated
      if ("title" in fields && (!fields.title || fields.title.trim() === "")) {
        return { success: false, error: "Target name is required" };
      }

      try {
        const response = await updateProgramReviewTargetMutation.mutateAsync({
          input: {
            targetId,
            ...fields,
          },
        });

        if (response.updateProgramReviewTarget?.status) {
          // Await refetch to ensure the data is updated before continuing
          // The selectedProgramReviewTarget will automatically update since it's derived from the query data
          await getProgramReviewGoalsQuery.refetch();
          onSuccess?.();
          return { success: true };
        }

        return { success: false, error: response.updateProgramReviewTarget?.message || "Failed to save changes" };
      } catch (error) {
        console.error("Error when saving target fields:", error);
        return { success: false, error: "Failed to save changes" };
      }
    },
    [updateProgramReviewTargetMutation, targetId, getProgramReviewGoalsQuery, onSuccess],
  );

  return {
    saveGoalFields,
    saveTargetFields,
    isSaving: updateProgramReviewGoalMutation.isPending || updateProgramReviewTargetMutation.isPending,
  };
};
